<template>
  <div class="ford-config-page">
    <header class="workspace-header">
      <div>
        <div class="eyebrow">FORD ADMIN</div>
        <h2>Ford 配置工作台</h2>
        <p>集中维护 Ford 后台开关、灰度名单和高风险配置。</p>
      </div>

      <el-tag type="primary" effect="plain" class="access-tag">仅 SSVIP 可见</el-tag>
    </header>

    <div class="workspace-body">
      <aside class="panel-nav" aria-label="Ford 配置面板">
        <button
          v-for="panel in panels"
          :key="panel.key"
          class="panel-nav-item"
          :class="{ active: activePanel === panel.key }"
          type="button"
          @click="activePanel = panel.key"
        >
          <el-icon>
            <component :is="panel.icon" />
          </el-icon>
          <span>
            <strong>{{ panel.title }}</strong>
            <small>{{ panel.desc }}</small>
          </span>
        </button>
      </aside>

      <main class="panel-stage">
        <KeepAlive>
          <component :is="currentPanel.component" />
        </KeepAlive>
      </main>
    </div>
  </div>
</template>

<script setup>
import { computed, h, ref } from 'vue';
import { Cpu, DocumentChecked, Filter, List, Operation, Setting, Switch as SwitchIcon, UserFilled, WarningFilled } from '@element-plus/icons-vue';

import DiagnosticSimulatorPanel from '@/components/ford/panels/DiagnosticSimulatorPanel.vue';
import FordSecurityCheckLogPanel from '@/components/ford/panels/FordSecurityCheckLogPanel.vue';
import SimulatorTemplateManagerPanel from '@/components/ford/panels/SimulatorTemplateManagerPanel.vue';
import UserPasswordAdminPanel from '@/components/ford/panels/UserPasswordAdminPanel.vue';
import V5075RedisSwitchPanel from '@/components/ford/panels/V5075RedisSwitchPanel.vue';
import FordRiskControlPanel from '@/components/ford/panels/FordRiskControlPanel.vue';
import DeliverStrategyPanel from '@/components/ford/panels/DeliverStrategyPanel.vue';
import FordRedisAdminPanel from '@/components/ford/panels/FordRedisAdminPanel.vue';
import VinFilterRulePanel from '@/components/ford/panels/VinFilterRulePanel.vue';

const activePanel = ref('diagnosticSimulator');

function createPlaceholderPanel(label) {
  return {
    name: 'FordPlaceholderPanel',
    setup() {
      return () => h('div', { class: 'empty-panel' }, label);
    },
  };
}

const panels = [
  {
    key: 'diagnosticSimulator',
    title: '模拟诊断请求',
    desc: '旧工具与新工具',
    icon: Cpu,
    component: DiagnosticSimulatorPanel,
  },
  {
    key: 'simulatorTemplates',
    title: '接口模板管理',
    desc: '维护 funcEx 模板',
    icon: Setting,
    component: SimulatorTemplateManagerPanel,
  },
  {
    key: 'v5075Redis',
    title: 'V5075 开关配置',
    desc: '新结构请求与 fallback',
    icon: SwitchIcon,
    component: V5075RedisSwitchPanel,
  },
  {
    key: 'riskControl',
    title: '短时风控配置',
    desc: '窗口与地区独立阈值',
    icon: WarningFilled,
    component: FordRiskControlPanel,
  },
  {
    key: 'vinFilterRules',
    title: 'VIN 过滤规则',
    desc: '江铃模板与测试',
    icon: Filter,
    component: VinFilterRulePanel,
  },
  {
    key: 'securityCheckLog',
    title: '安全校验日志',
    desc: '失败请求分析',
    icon: WarningFilled,
    component: FordSecurityCheckLogPanel,
  },
  {
    key: 'userPasswordAdmin',
    title: '用户密码管理',
    desc: '注册与密码',
    icon: UserFilled,
    component: UserPasswordAdminPanel,
  },
  {
    key: 'redisQuery',
    title: 'Redis Key 查询',
    desc: '预留',
    icon: List,
    component: createPlaceholderPanel('Redis Key 查询'),
  },
  {
    key: 'deliverPolicy',
    title: 'Deliver 数据策略',
    desc: '规则与发布',
    icon: Operation,
    component: DeliverStrategyPanel,
  },
  {
    key: 'auditLog',
    title: '操作日志',
    desc: '预留',
    icon: DocumentChecked,
    component: createPlaceholderPanel('操作日志'),
  },
];

const currentPanel = computed(() => {
  if (activePanel.value === 'redisQuery') {
    return { component: FordRedisAdminPanel };
  }
  return panels.find((panel) => panel.key === activePanel.value) || panels[0];
});
</script>

<style scoped>
.ford-config-page {
  min-height: 100%;
  min-width: 0;
  padding: 22px 26px 32px;
  box-sizing: border-box;
  overflow-x: hidden;
  background:
    linear-gradient(180deg, #f7f9fc 0%, #eef3f8 100%);
}

.workspace-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px;
  margin-bottom: 16px;
  border: 1px solid #dfe8f3;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 10px 26px rgba(31, 56, 88, 0.06);
}

.eyebrow {
  margin-bottom: 6px;
  color: #0f766e;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0;
}

.workspace-header h2 {
  margin: 0;
  color: #132033;
  font-size: 22px;
  font-weight: 800;
  letter-spacing: 0;
}

.workspace-header p {
  margin: 6px 0 0;
  color: #65758a;
  font-size: 13px;
  line-height: 1.5;
}

.access-tag {
  flex: 0 0 auto;
  height: 30px;
  border-radius: 999px;
  font-weight: 800;
}

.workspace-body {
  display: grid;
  grid-template-columns: 230px minmax(0, 1fr);
  gap: 16px;
  align-items: start;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  box-sizing: border-box;
}

.panel-nav {
  display: grid;
  gap: 10px;
  min-width: 0;
}

.panel-nav-item {
  width: 100%;
  min-height: 64px;
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  padding: 10px;
  border: 1px solid #dfe8f3;
  border-radius: 8px;
  background: #ffffff;
  color: #4b5f75;
  text-align: left;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(31, 56, 88, 0.04);
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.panel-nav-item:hover {
  transform: translateY(-1px);
  border-color: #b7d4f6;
  box-shadow: 0 12px 24px rgba(31, 56, 88, 0.08);
}

.panel-nav-item.active {
  color: #0f3f74;
  border-color: #93c5fd;
  background: #f0f7ff;
  box-shadow: inset 3px 0 0 #3b82f6, 0 12px 24px rgba(37, 99, 235, 0.08);
}

.panel-nav-item .el-icon {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #0f766e;
  background: #ecfdf5;
}

.panel-nav-item strong,
.panel-nav-item small {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.panel-nav-item strong {
  font-size: 14px;
  line-height: 1.2;
}

.panel-nav-item small {
  margin-top: 4px;
  color: #7a8a9d;
  font-size: 12px;
  line-height: 1.2;
}

.panel-stage {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  box-sizing: border-box;
}

.empty-panel {
  min-height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed #c8d6e6;
  border-radius: 8px;
  color: #7a8a9d;
  background: #ffffff;
  font-weight: 700;
}

@media (max-width: 900px) {
  .ford-config-page {
    padding: 14px;
  }

  .workspace-header {
    flex-direction: column;
  }

  .workspace-body {
    grid-template-columns: 1fr;
  }

  .panel-nav {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 560px) {
  .panel-nav {
    grid-template-columns: 1fr;
  }
}
</style>
